import React, { createContext, ReactNode } from 'react';
import { MsalAuthenticationTemplate, MsalProvider, useMsal } from '@azure/msal-react';
import { InteractionType, PublicClientApplication } from '@azure/msal-browser';

export interface BoschAuthProviderProps {
  children: ReactNode;
  clientId: string;
  redirectUri: (location: Location) => string;
  authority: string;
  errorComponent: ReactNode;
  scopes: string[];
  enableLocally?: boolean;
}

const BoschAuthContext = createContext<{ scopes: string[] }>({ scopes: [] });

export function BoschAuthProvider({
  children,
  clientId,
  redirectUri,
  authority,
  errorComponent,
  scopes,
  enableLocally,
}: BoschAuthProviderProps): ReactNode {
  if (clientId) {
    if (typeof location !== 'undefined' && (process.env.NODE_ENV === 'production' || enableLocally)) {
      if (location.hostname === 'localhost' && !enableLocally) {
        // skip auth for localhost (also required for testing)
        return children;
      }

      const pca = new PublicClientApplication({
        auth: {
          clientId: clientId,
          redirectUri: redirectUri(location),
          authority: authority,
        },
        cache: {
          cacheLocation: 'localStorage',
        },
      });

      return (
        <MsalProvider instance={pca}>
          <MsalAuthenticationTemplate
            interactionType={InteractionType.Redirect}
            authenticationRequest={{ scopes }}
            errorComponent={() => errorComponent}
          >
            <BoschAuthContext.Provider value={{ scopes }}>{children}</BoschAuthContext.Provider>
          </MsalAuthenticationTemplate>
        </MsalProvider>
      );
    } else {
      return <div></div>; // this basically removes all benefits of SSG, but this is expected when hiding the content behind authentication
    }
  } else {
    // auth disabled
    return children;
  }
}

export function useBoschAuth(): { getToken: () => Promise<string> } {
  const { instance, accounts } = useMsal();
  const { scopes } = React.useContext(BoschAuthContext);

  const getToken = async (): Promise<string> => {
    if (!accounts[0]) {
      return Promise.reject({ status: 401, error: 'No account found' });
    }

    const result = await instance.acquireTokenSilent({ scopes, account: accounts[0] });
    return result.accessToken;
  };

  return { getToken };
}
